/*
 * Per-suggestion serial queue for a staged suggestion's accept SIDE EFFECTS —
 * the undo compensation (unlink Encounter.diagnosis) and the redo re-apply
 * (relink it). Both are launched from the reconciler's synchronous transaction
 * handler, so a rapid Cmd+Z then Cmd+Shift+Z starts the second before the first
 * has reached its FHIR write. Unserialized, the redo can observe the encounter
 * still linked, return early as an idempotent no-op, and let the undo's unlink
 * commit AFTER it — leaving accepted content in the note with no diagnosis link
 * and no later pass that repairs it.
 *
 * Two guarantees, keyed per suggestion id:
 *
 *   1. SERIAL — two tasks for one suggestion never overlap.
 *   2. LATEST WINS — a task superseded while it waited is dropped rather than
 *      run, so an older desired state can never land after a newer one. Sound
 *      because each direction reconciles that suggestion's whole FHIR state
 *      against the document, so the newest enqueue alone converges.
 *
 * Per-suggestion, not global: two suggestions own disjoint receipts and must
 * still proceed concurrently.
 */
export interface SuggestionSideEffectQueue {
  /* Resolves once the task has run or been superseded. Never rejects — the
   * compensation / re-apply wiring contains its own failures, and a rejected
   * tail would strand every later task for that suggestion. */
  run: (id: string, task: () => Promise<void>) => Promise<void>
}

export function createSuggestionSideEffectQueue(): SuggestionSideEffectQueue {
  const tails = new Map<string, Promise<void>>()
  const latest = new Map<string, number>()
  let issued = 0

  return {
    run(id, task) {
      const ticket = ++issued
      latest.set(id, ticket)
      const settled = (tails.get(id) ?? Promise.resolve())
        .then(async () => {
          if (latest.get(id) !== ticket) {
            return
          }
          await task()
        })
        .catch(() => {})
      tails.set(id, settled)
      void settled.then(() => {
        // Only the tail clears the entry; a task chained on behind this one has
        // already replaced it.
        if (tails.get(id) === settled) {
          tails.delete(id)
          latest.delete(id)
        }
      })
      return settled
    },
  }
}
